import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Users, Plus, Search, Phone, Mail, Home, User, Building2,
  Calendar, ChevronRight, Filter, Loader2, UserPlus, MapPin
} from "lucide-react";
import { API } from "@/App";
import { PostcodeLookup } from "@/components/PostcodeLookup";

const ROLES = [
  { value: "seller", label: "Seller", color: "bg-blue-100 text-blue-700" },
  { value: "buyer", label: "Buyer", color: "bg-green-100 text-green-700" },
  { value: "landlord", label: "Landlord", color: "bg-purple-100 text-purple-700" },
  { value: "tenant", label: "Tenant", color: "bg-amber-100 text-amber-700" }
];

const BUYER_POSITIONS = [
  { value: "ftb", label: "First Time Buyer (FTB)" },
  { value: "has_property", label: "Has Property to Sell" },
  { value: "sold_stc", label: "Sold (STC)" },
  { value: "nothing_to_sell", label: "Nothing to Sell (Cash/Investor)" }
];

export const ContactsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [showNewContact, setShowNewContact] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staffList, setStaffList] = useState([]);
  
  // New contact form
  const [formData, setFormData] = useState({
    title: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    mobile: "",
    address_line1: "",
    address_line2: "",
    town: "",
    county: "",
    postcode: "",
    roles: [],
    assigned_negotiator: "",
    branch: "Head Office",
    notes: "",
    // Buyer specific
    buyer_position: "",
    buyer_budget_min: "",
    buyer_budget_max: "",
    buyer_bedrooms_min: "",
    buyer_bedrooms_max: "",
    buyer_areas: [],
    buyer_property_types: []
  });

  const fetchContacts = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams();
      
      if (searchQuery) params.append("search", searchQuery);
      if (roleFilter && roleFilter !== "all") params.append("role", roleFilter);
      
      const res = await axios.get(`${API}/contacts?${params.toString()}`, { headers });
      setContacts(res.data?.contacts || []);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    } finally {
      setLoading(false);
    }
  }, [token, searchQuery, roleFilter]);

  const fetchStaff = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/staff`, { headers });
      setStaffList(res.data?.staff || []);
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchContacts();
    fetchStaff();
  }, [fetchContacts, fetchStaff]);

  const handleRoleToggle = (role) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
  };

  const handleCreateContact = async () => {
    if (!formData.first_name || !formData.last_name) {
      toast.error("Please enter first and last name");
      return;
    }
    
    if (formData.roles.length === 0) {
      toast.error("Please select at least one role");
      return;
    }
    
    // Validate buyer position if buyer selected
    if (formData.roles.includes("buyer") && !formData.buyer_position) {
      toast.error("Please select buyer position");
      return;
    }
    
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const payload = {
        ...formData,
        buyer_budget_min: formData.buyer_budget_min ? parseInt(formData.buyer_budget_min) : null,
        buyer_budget_max: formData.buyer_budget_max ? parseInt(formData.buyer_budget_max) : null,
        buyer_bedrooms_min: formData.buyer_bedrooms_min ? parseInt(formData.buyer_bedrooms_min) : null,
        buyer_bedrooms_max: formData.buyer_bedrooms_max ? parseInt(formData.buyer_bedrooms_max) : null
      };
      
      const res = await axios.post(`${API}/contacts`, payload, { headers });
      
      toast.success("Contact created successfully");
      setShowNewContact(false);
      resetForm();
      fetchContacts();
      
      // Navigate to contact detail
      if (res.data?.contact?.contact_id) {
        navigate(`/contacts/${res.data.contact.contact_id}`);
      }
    } catch (error) {
      console.error("Error creating contact:", error);
      toast.error(error.response?.data?.detail || "Failed to create contact");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "", first_name: "", last_name: "", email: "", phone: "", mobile: "",
      address_line1: "", address_line2: "", town: "", county: "", postcode: "",
      roles: [], assigned_negotiator: "", branch: "Head Office", notes: "",
      buyer_position: "", buyer_budget_min: "", buyer_budget_max: "",
      buyer_bedrooms_min: "", buyer_bedrooms_max: "", buyer_areas: [], buyer_property_types: []
    });
  };

  const getRoleBadges = (roles) => {
    return roles?.map(role => {
      const roleConfig = ROLES.find(r => r.value === role);
      return (
        <Badge key={role} className={roleConfig?.color || "bg-slate-100"}>
          {roleConfig?.label || role}
        </Badge>
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-emerald-600" />
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Contacts</h1>
                <p className="text-sm text-slate-500">Manage all your clients in one place</p>
              </div>
            </div>
            <Button onClick={() => setShowNewContact(true)} data-testid="new-contact-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              New Contact
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, phone, postcode..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-40">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {ROLES.map(role => (
                <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Contact List */}
        <div className="grid gap-3">
          {contacts.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-muted-foreground">No contacts found</p>
                <Button variant="outline" className="mt-4" onClick={() => setShowNewContact(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add your first contact
                </Button>
              </CardContent>
            </Card>
          ) : (
            contacts.map((contact) => (
              <Card 
                key={contact.contact_id} 
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/contacts/${contact.contact_id}`)}
                data-testid={`contact-${contact.contact_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                        <User className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-800">{contact.full_name}</h3>
                          <div className="flex gap-1">
                            {getRoleBadges(contact.roles)}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                          {contact.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {contact.email}
                            </span>
                          )}
                          {contact.mobile && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {contact.mobile}
                            </span>
                          )}
                          {contact.address?.postcode && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {contact.address.postcode}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* New Contact Dialog */}
      <Dialog open={showNewContact} onOpenChange={setShowNewContact}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              New Contact
            </DialogTitle>
            <DialogDescription>
              Register a new client. Select their role(s) to capture relevant details.
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6 py-4">
              {/* Role Selection */}
              <div>
                <Label className="text-base font-semibold">Client Role(s) *</Label>
                <p className="text-sm text-slate-500 mb-3">Select all that apply</p>
                <div className="flex flex-wrap gap-3">
                  {ROLES.map(role => (
                    <div
                      key={role.value}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                        formData.roles.includes(role.value)
                          ? `${role.color} border-current`
                          : "bg-white border-slate-200 hover:border-slate-300"
                      }`}
                      onClick={() => handleRoleToggle(role.value)}
                    >
                      <Checkbox 
                        checked={formData.roles.includes(role.value)}
                        onCheckedChange={() => handleRoleToggle(role.value)}
                      />
                      <span className="font-medium">{role.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Basic Details */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label>Title</Label>
                  <Select value={formData.title} onValueChange={(v) => setFormData({...formData, title: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mr">Mr</SelectItem>
                      <SelectItem value="Mrs">Mrs</SelectItem>
                      <SelectItem value="Miss">Miss</SelectItem>
                      <SelectItem value="Ms">Ms</SelectItem>
                      <SelectItem value="Dr">Dr</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-1">
                  <Label>First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Last Name *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Contact Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Mobile</Label>
                  <Input
                    value={formData.mobile}
                    onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Branch</Label>
                  <Select value={formData.branch} onValueChange={(v) => setFormData({...formData, branch: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Head Office">Head Office</SelectItem>
                      <SelectItem value="Bedford Office">Bedford Office</SelectItem>
                      <SelectItem value="Kempston Office">Kempston Office</SelectItem>
                      <SelectItem value="Cranfield Office">Cranfield Office</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Address with Postcode Lookup */}
              <div>
                <Label className="text-base font-semibold mb-2 block">Address</Label>
                <PostcodeLookup
                  onAddressSelect={(addr) => {
                    setFormData({
                      ...formData,
                      address_line1: addr.line_1 + (addr.line_2 ? ", " + addr.line_2 : ""),
                      address_line2: addr.line_3 || "",
                      town: addr.town_or_city || "",
                      county: addr.county || "",
                      postcode: addr.postcode || ""
                    });
                  }}
                />
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div className="col-span-2">
                    <Label>Address Line 1</Label>
                    <Input
                      value={formData.address_line1}
                      onChange={(e) => setFormData({...formData, address_line1: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Town</Label>
                    <Input
                      value={formData.town}
                      onChange={(e) => setFormData({...formData, town: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Postcode</Label>
                    <Input
                      value={formData.postcode}
                      onChange={(e) => setFormData({...formData, postcode: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Buyer Position (only if buyer selected) */}
              {formData.roles.includes("buyer") && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <Label className="text-base font-semibold text-green-800 mb-3 block">
                    Buyer Details
                  </Label>
                  <div className="space-y-4">
                    <div>
                      <Label>Position *</Label>
                      <Select 
                        value={formData.buyer_position} 
                        onValueChange={(v) => setFormData({...formData, buyer_position: v})}
                      >
                        <SelectTrigger className="mt-1 bg-white">
                          <SelectValue placeholder="Select buyer position" />
                        </SelectTrigger>
                        <SelectContent>
                          {BUYER_POSITIONS.map(pos => (
                            <SelectItem key={pos.value} value={pos.value}>{pos.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Budget Min</Label>
                        <Input
                          type="number"
                          placeholder="£"
                          value={formData.buyer_budget_min}
                          onChange={(e) => setFormData({...formData, buyer_budget_min: e.target.value})}
                          className="mt-1 bg-white"
                        />
                      </div>
                      <div>
                        <Label>Budget Max</Label>
                        <Input
                          type="number"
                          placeholder="£"
                          value={formData.buyer_budget_max}
                          onChange={(e) => setFormData({...formData, buyer_budget_max: e.target.value})}
                          className="mt-1 bg-white"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Min Bedrooms</Label>
                        <Input
                          type="number"
                          value={formData.buyer_bedrooms_min}
                          onChange={(e) => setFormData({...formData, buyer_bedrooms_min: e.target.value})}
                          className="mt-1 bg-white"
                        />
                      </div>
                      <div>
                        <Label>Max Bedrooms</Label>
                        <Input
                          type="number"
                          value={formData.buyer_bedrooms_max}
                          onChange={(e) => setFormData({...formData, buyer_bedrooms_max: e.target.value})}
                          className="mt-1 bg-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Assignment */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Assigned Negotiator</Label>
                  <Select 
                    value={formData.assigned_negotiator} 
                    onValueChange={(v) => setFormData({...formData, assigned_negotiator: v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select negotiator" />
                    </SelectTrigger>
                    <SelectContent>
                      {staffList.map(staff => (
                        <SelectItem key={staff.user_id} value={staff.name}>{staff.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="mt-1"
                  rows={3}
                  placeholder="Any additional notes about this contact..."
                />
              </div>
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNewContact(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateContact} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactsPage;
